/**
 * Shared helper for building authenticated requests to the chat backend.
 *
 * Centralizes:
 *   - Bearer-token acquisition (DB JWT in localStorage OR an Entra
 *     backend-audience access token from MSAL)
 *   - Identity headers (X-User-Email / X-User-Username)
 *
 * Why the identity headers matter: most Entra access tokens issued for the
 * backend's API audience omit `preferred_username` / `email` claims, so the
 * backend's auth middleware (see backend/src/middleware/auth.ts) falls back
 * to the literal string 'unknown' for username/email when those headers are
 * absent. That bubbles up to the analytics dashboard as a flood of
 * "Unknown" rows. Forwarding what MSAL already knows (`account.username`
 * for email, `account.name` for display name) lets the SSO-user upsert
 * heal the row on every authenticated request.
 *
 * DB-auth users don't need identity headers — their record is fully
 * populated at login time — so we only return the token for them.
 *
 * The dashboard has the same helper at dashboard/src/services/authApi.ts
 * (`getBackendAuth`); this is the chat-frontend mirror.
 */

import { msalInstance, getActiveAccount } from './authService';

export interface IdentityHeaders {
  email?: string;
  username?: string;
  /**
   * Microsoft Graph `/me?$select=department`. Sourced at sign-in by
   * AuthContext and cached in localStorage as `user_department`.
   * Forwarded so the backend can sync `users.department` without
   * admins editing each user manually.
   */
  department?: string;
}

export interface BackendAuth {
  token: string;
  identity?: IdentityHeaders;
}

function getBackendClientId(): string | null {
  const fromRuntime = (
    window as Window & { RUNTIME_CONFIG?: { BACKEND_CLIENT_ID?: string } }
  ).RUNTIME_CONFIG?.BACKEND_CLIENT_ID;
  return (
    fromRuntime ||
    import.meta.env.VITE_BACKEND_CLIENT_ID ||
    import.meta.env.VITE_AZURE_CLIENT_ID ||
    null
  );
}

/**
 * Resolve the bearer token + (for SSO) identity hints for a backend call.
 *
 * Priority:
 *   1. DB JWT in localStorage (returned without identity — DB users have
 *      a fully populated `users` row).
 *   2. Entra access token via MSAL `acquireTokenSilent`, plus identity
 *      pulled from the MSAL account.
 *
 * Returns `null` when neither path resolves to a usable token. Callers
 * should treat that the same way they treat a 401 (skip the call, or let
 * the request fire and let the backend reject it). Errors during MSAL
 * token acquisition are caught and turned into `null` — telemetry must
 * never break the user-facing flow.
 */
export async function getBackendAuth(): Promise<BackendAuth | null> {
  const dbToken = localStorage.getItem('db_auth_token');
  if (dbToken) {
    return { token: dbToken };
  }

  try {
    const account = getActiveAccount();
    if (!account) return null;

    const backendClientId = getBackendClientId();
    if (!backendClientId) return null;

    const response = await msalInstance.acquireTokenSilent({
      scopes: [`api://${backendClientId}/.default`],
      account,
    });

    const department = localStorage.getItem('user_department') ?? undefined;
    return {
      token: response.accessToken,
      identity: {
        email: account.username,
        username: account.name ?? account.username,
        ...(department ? { department } : {}),
      },
    };
  } catch {
    return null;
  }
}

/**
 * Build the standard header map for a chat-backend request:
 *   - Content-Type: application/json
 *   - Authorization (when a token is available)
 *   - X-User-Email / X-User-Username (when identity is known)
 *
 * Pass `extra` to merge in caller-specific headers (e.g. agentService
 * adds X-User-Groups). `extra` always wins over the defaults.
 *
 * Never throws. When no auth is available, returns headers without an
 * Authorization line so the caller's existing 401 handling kicks in.
 */
export async function getBackendHeaders(
  extra?: Record<string, string>
): Promise<Record<string, string>> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const auth = await getBackendAuth();
  if (auth) {
    headers.Authorization = `Bearer ${auth.token}`;
    if (auth.identity?.email) headers['X-User-Email'] = auth.identity.email;
    if (auth.identity?.username) headers['X-User-Username'] = auth.identity.username;
    if (auth.identity?.department) {
      headers['X-User-Department'] = auth.identity.department;
    }
  }
  if (extra) {
    for (const [k, v] of Object.entries(extra)) {
      headers[k] = v;
    }
  }
  return headers;
}
